import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui-kit/badge';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useRoleUsers } from '../../hooks/use-roles';
import { OrganizationRoleRow } from '../../types/role.types';

export interface RoleUsersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: OrganizationRoleRow | null;
}

/** The people who hold a role in the current organization, read-only. */
export const RoleUsersDialog = ({ open, onOpenChange, row }: RoleUsersDialogProps) => {
  const { t } = useTranslation();
  const { data: users = [], isLoading } = useRoleUsers(open && row ? row.role.slug : null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;
    return users.filter((user) =>
      `${user.firstName ?? ''} ${user.lastName ?? ''} ${user.email}`.toLowerCase().includes(term)
    );
  }, [users, search]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {t('ROLE_USERS')}: {row?.role.name}
          </DialogTitle>
          <DialogDescription>
            {isLoading
              ? t('ROLE_USERS_DESCRIPTION')
              : t('ROLE_USERS_COUNT', { count: users.length })}
          </DialogDescription>
        </DialogHeader>

        <Input
          placeholder={t('SEARCH')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        <div className="max-h-[60vh] overflow-y-auto pr-1">
          {isLoading ? (
            <Skeleton className="h-48 w-full rounded-md" />
          ) : shown.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{t('NO_DATA_FOUND')}</p>
          ) : (
            <ul className="divide-y rounded-md border">
              {shown.map((user) => (
                <li key={user.itemId} className="flex items-center justify-between gap-3 px-3 py-2">
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">
                      {[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {user.email}
                    </span>
                  </span>
                  {user.active === false && <Badge variant="info-subtle">{t('INACTIVE')}</Badge>}
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <DialogCancelButton onClick={() => onOpenChange(false)}>{t('CLOSE')}</DialogCancelButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
